import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'next-i18next';
import { FormProvider, useForm } from 'react-hook-form';

import { HeaderTitleInteractionBox, StyleButton, StyleRawTypography } from '../style';

import EssayForm, { InteractiveEssayFields } from './essayForm';

import InteractiveContentModal, {
  InteractiveContentModalProps,
} from '@components/atoms/dialog/interactiveContentModal';
import PlayCircleIcon from '@components/atoms/icon/playCircleIcon';
import { IInteractiveContentSubmissions, IInteractiveContent, InteractionType, IQuiz } from '@interfaces/learning';
import InteractiveContentActions from '@redux/interactiveContent/actions';
import { RootState } from '@redux/reducer';
import learning from 'src/api/learning';
import { sanitize } from 'src/helpers/utils';
import theme from 'src/theme';

export type InteractiveEssayProps = Pick<InteractiveContentModalProps, 'onClose' | 'containerRef' | 'open'> & {
  interactiveContent: IInteractiveContent;
  isPreview?: boolean;
};

const InteractiveEssay = ({ open, interactiveContent, onClose, containerRef, isPreview }: InteractiveEssayProps) => {
  const content = interactiveContent.content as IQuiz;
  const { name, question_sets } = content;
  const questionSet = question_sets?.[0];
  const question = questionSet?.question_sets_questions?.[0].content;

  const dispatch = useDispatch();
  const { t } = useTranslation();

  const { interactiveContentSubmission, isInteractiveContentSubmissionLoading } = useSelector(
    (state: RootState) => state.InteractContent,
  );

  const methods = useForm<InteractiveEssayFields>({
    defaultValues: {
      answer: '',
    },
  });

  const { getValues, setError } = methods;

  const answer = Object.keys(getValues('answer')).length;

  const startInteractionContent = async () => {
    const started_at = new Date().toISOString();
    const total_score = Object.keys(getValues('answer')).length;
    const payload = {
      type: InteractionType.ESSAY,
      question_set_id: questionSet?.id,
      started_at,
      total_score,
    };

    dispatch(InteractiveContentActions.start_interactive_content(interactiveContent.id, payload));
  };

  const handleSubmit = async (data: InteractiveEssayFields) => {
    if (!isPreview && interactiveContentSubmission?.id) {
      try {
        const payload = {
          type: InteractionType.ESSAY,
          question_set_id: questionSet?.id,
          finished_at: new Date().toISOString(),
          answers: [
            {
              question_id: question?.id,
              user_answer_text: data.answer,
            },
          ],
        };
        dispatch(
          InteractiveContentActions.submit_interactive_content(
            interactiveContent.id,
            interactiveContentSubmission.id,
            payload,
          ),
        );
      } catch (e) {
        methods.setError('answer', { type: 'server', message: 'Something went wrong' });
      }
    }
  };

  useEffect(() => {
    if (!isInteractiveContentSubmissionLoading && open && !interactiveContentSubmission) {
      startInteractionContent();
    }
  }, [open]);

  return (
    <InteractiveContentModal open={open} containerRef={containerRef} onClose={onClose}>
      {!question ? (
        <Typography>Failed to load question</Typography>
      ) : (
        <FormProvider {...methods}>
          <Box component="form" height="100%" onSubmit={methods.handleSubmit(handleSubmit)}>
            <Box
              p={5}
              display="flex"
              flexDirection="column"
              justifyContent="space-between"
              height="100%"
              overflow="auto"
            >
              {!methods.formState.isSubmitted ? (
                <EssayForm content={content} />
              ) : (
                <>
                  <Box>
                    <HeaderTitleInteractionBox mb={3}>
                      <img src="/static/icons/correct-icon.svg" className="svg-icon" />
                      <Box style={{ marginLeft: '16px' }}>
                        <Typography component="div" fontWeight={500} fontSize={24} color="#000000DE">
                          {t('interactive.essay_form.rule')}
                        </Typography>
                        <Typography fontSize={{ xs: 36, md: 48 }} fontWeight={700} lineHeight="43px" color="#000000DE">
                          {t('interactive.essay_form.success.title')}
                        </Typography>
                      </Box>
                    </HeaderTitleInteractionBox>
                    <StyleRawTypography fontSize={24} dangerouslySetInnerHTML={sanitize(question.name)} />
                    <Typography fontSize={18} color="000000DE" whiteSpace="pre-wrap" fontFamily="Lanna" mt={1}>
                      {methods.getValues('answer')}
                    </Typography>
                  </Box>
                  <Box
                    alignSelf={{
                      xs: 'stretch',
                      md: 'flex-end',
                    }}
                  >
                    <StyleButton
                      variant="contained"
                      onClick={onClose}
                      sx={{
                        mt: 3,
                        [theme.breakpoints.down('md')]: {
                          width: '100%',
                        },
                      }}
                    >
                      <PlayCircleIcon sx={{ mr: 1.5, fontSize: '32px' }} />
                      {t('interactive.essay_form.button.continue')}
                    </StyleButton>
                  </Box>
                </>
              )}
            </Box>
          </Box>
        </FormProvider>
      )}
    </InteractiveContentModal>
  );
};

export default InteractiveEssay;
